import extend from 'lodash/extend';

window.atex = window.atex || {};
window.atex.aceui = window.atex.aceui || {};

const types = {
  AUTHENTICATION_FAILED: 'authenticationFailed',
  BAD_GATEWAY: 'badGateway',
  CONFIG_ERROR: 'configurationError',
  CONFLICT: 'conflict',
  DATA_ERROR: 'dataError',
  INVALID_PARAMETERS: 'invalidParameters',
  NOT_IMPLEMENTED: 'notImplemented',
  NOT_FOUND: 'notFound',
  OPERATION_CANCELLED: 'operationCancelled',
  PERMISSION_DENIED: 'permissionDenied',
  REQUEST_FAILED: 'requestFailed',
  RUNTIME: 'runtime',
  SERVER_ERROR: 'serverError',
  STORAGE_ERROR: 'storageError',
  TIMEOUT: 'timeout',
  UNKNOWN: 'unknown',
  VALIDATION_FAILED: 'validationFailed',
  VERSION_NOT_FOUND: 'versionNotFound'
};

const severities = {
  /** A negligible error. */
  MINIMAL: 'minimal',
  /** An error that does not affect data integrity or put the application in an insecure state */
  MINOR: 'minor',
  /** A serious error. Might compromise or corrupt data, may cause succeeding errors. */
  MAJOR: 'major',
  /** A system failure. The application is unusable. */
  CRITICAL: 'critical'
};

const Error = ({
  cause = defaultError.cause,
  origin = defaultError.origin,
  rootCause,
  severity = defaultError.severity,
  type = defaultError.type
} = defaultError) => {
  return Object.assign ({}, {
    id: atex.aceui.Utils.uuid (),
    date: new Date (),
    origin,
    cause,
    rootCause,
    type,
    severity
  });
};

const DerivedError = (derivedError, errorInformationObject) => {
  var infoObject = errorInformationObject || {};
  var id = atex.aceui.Utils.uuid();
  return extend({}, derivedError, { id: id, date: new Date(), rootCause: derivedError }, infoObject);
};

atex.aceui.error = {};

/**
 * The atex.aceui.error.types object is an enumeration of error types typically
 * used in a AceUI application. The type property of a atex.aceui.error.Error
 * object will typically be one of the enumeration values.
 */
atex.aceui.error.types = types;

/**
 * The atex.aceui.error.severities object is an enumeration of error severities typically used by an AceUI
 * application. The severity property of a atex.aceui.error.Error object should be one of the enumeration values.
 * @readonly
 * @enum {string}
 */
atex.aceui.error.severities = severities;

/**
 * The default Error object used as base when creating new ones. Has default
 * (although very generic) values for most properties.
 */
const defaultError = {
  origin: 'Unknown',
  cause: 'An error has occurred!',
  type: atex.aceui.error.types.UNKNOWN,
  severity: atex.aceui.error.severities.MINOR
};

/**
 * Creates a new Error object.
 *
 * Argument 'errorInformationObject' - An optional parameter object which contains
 * error information.
 *
 * If 'errorInformationObject' is not passed in, the returned atex.aceui.error.Error object
 * will look like this:
 *
 * {
 *   "id": <random id string>,
 *   "date": new Date(),
 *   "cause": "An error has occurred!",
 *   "origin": "Unknown",
 *   "type": atex.aceui.error.types.UNKNOWN,
 *   "severity": atex.aceui.error.severities.MINOR
 * }
 *
 * Returns a new Error object.
 */
atex.aceui.error.Error = Error;

/**
 * Creates a new Error object based on an existing Error object.
 *
 * All properties from the derived Error object are copied to the new Error object. A new 'date' attribute is set
 * on the Error object and its 'rootCause' will be the derived Error. Lastly all properties of the errorInformationObject
 * will be copied to the new Error.
 *
 * @param derivedError The derived Error object
 * @param errorInformationObject An optional object containing error information. Any properties it contains
 * will also be included in the returned atex.aceui.error.Error object.
 * @returns a new Error object
 */
atex.aceui.error.DerivedError = DerivedError;

export default { types, severities, Error, DerivedError };
